import {
  getConsoleRunDisplayTimezone,
  getRunDayTimeSlotInTimezone,
} from "./dateTimeUtils";
import { eventTypeToRunType } from "./consoleUtils";
import { getWorkflowStatus } from "./consoleRunsConstants";

/** Map difficulty (easy/steady/spicy) to effortLevel. */
export function difficultyToEffortLevel(difficulty) {
  if (!difficulty) return "relaxed";
  const d = String(difficulty).toLowerCase();
  if (d === "easy") return "relaxed";
  if (d === "steady") return "moderate";
  if (d === "spicy") return "challenging";
  return "relaxed";
}

/** Publish gate: errors that block publishing (reference: runPublishGate). */
export function runPublishGate(run, clubById) {
  const errors = [];
  if (!run?.club_id) errors.push("Club must be linked");
  const club = run?.club_id ? clubById[run.club_id] : null;
  if (run?.club_id && !club) errors.push("Linked club not found");
  if (!run?.day) errors.push("Day is required");
  if (!run?.time) errors.push("Start time is required");
  if (!run?.startLocation || String(run.startLocation).trim().length < 2) errors.push("Exact location is required");
  if (!run?.recurrence) errors.push("Recurrence must be defined");
  return errors;
}

/** Labels match ConsoleRunFormModal — used for tooltips and “missing for 100%” copy. Pace ranges are optional and not part of completeness. */
export const RUN_COMPLETENESS_GATE_LABELS = {
  name: "Run name",
  location: "Address (full meet-up)",
  notes: "What to expect",
  image: "Run image(s)",
};

/** Completeness fields and percentage (single source of truth for name, location, notes, image). */
export function getRunCompletenessWithFields(run) {
  if (!run) {
    const compFields = Object.keys(RUN_COMPLETENESS_GATE_LABELS).map((key) => ({ key, ok: false }));
    const missingLabels = Object.values(RUN_COMPLETENESS_GATE_LABELS);
    return { pct: 0, compFields, missingLabels };
  }
  const compFields = [
    { key: "name", ok: Boolean(run.name && String(run.name).trim().length > 1) },
    { key: "location", ok: Boolean(run.address && String(run.address).trim().length > 1) },
    { key: "notes", ok: Boolean(run.what_to_expect && String(run.what_to_expect).trim().length > 1) },
    { key: "image", ok: Boolean(Array.isArray(run.images) && run.images.length > 0) },
  ];
  const total = compFields.length;
  const okCount = compFields.filter((f) => f.ok).length;
  const pct = total > 0 ? Math.round((okCount / total) * 100) : 0;
  const missingLabels = compFields.filter((f) => !f.ok).map((f) => RUN_COMPLETENESS_GATE_LABELS[f.key] ?? f.key);
  return { pct, compFields, missingLabels };
}

/** Completeness 0–100% from name, address, notes, image. */
export function runCompleteness(run) {
  return getRunCompletenessWithFields(run).pct;
}

/** Recurring series (not one-time). Used for table collapse and delete dialog. */
export function isRecurringSeriesMeeting(m) {
  if (!m?.is_recurring) return false;
  return m.recurrence_pattern !== "one-time";
}

/** Template id for a recurring row: parent_meeting_id for instances, else own id. */
export function recurringSeriesTemplateId(m) {
  if (m?.id == null || m.id === "") return "";
  const pid = m.parent_meeting_id;
  if (pid != null && String(pid).trim() !== "") return String(pid).trim();
  return String(m.id).trim();
}

export function meetingScheduledTimeMs(m) {
  if (!m?.scheduled_date) return NaN;
  const t = new Date(m.scheduled_date).getTime();
  return Number.isNaN(t) ? NaN : t;
}

/** Same as {@link isRecurringSeriesMeeting} for run rows (spread may include recurrence + recurrence_pattern). */
export function isConsoleRunRecurringSeries(run) {
  if (!run?.is_recurring) return false;
  const p = run.recurrence_pattern ?? run.recurrence;
  return p !== "one-time";
}

/** Short label for the occurrence shown in the table (schedule column). */
export function formatRunScheduleOccurrenceLabel(run) {
  if (!run) return "this occurrence";
  const head = [
    run.day,
    run.slot ? String(run.slot).toUpperCase() : null,
    run.time && String(run.time).trim() !== "" ? run.time.trim() : null,
  ]
    .filter(Boolean)
    .join(" ");
  const tail = run.recurrence && run.recurrence !== "one-time" ? run.recurrence : null;
  if (head && tail) return `${head} · ${tail}`;
  if (head) return head;
  return "this occurrence";
}

/**
 * One row per recurring series in the Runs table: merge template + instances that appear in the
 * current list. Uses the earliest scheduled_date in the window for schedule display; id is always
 * the series template id for navigation, edit, and detail.
 */
export function collapseRecurringMeetingsForRunsTable(meetings) {
  if (!Array.isArray(meetings) || meetings.length === 0) return [];

  const seriesGroups = new Map();
  for (const m of meetings) {
    if (!isRecurringSeriesMeeting(m)) continue;
    const sid = recurringSeriesTemplateId(m);
    if (!sid) continue;
    if (!seriesGroups.has(sid)) seriesGroups.set(sid, []);
    seriesGroups.get(sid).push(m);
  }

  const out = [];
  const seenSeries = new Set();

  for (const m of meetings) {
    if (!isRecurringSeriesMeeting(m)) {
      out.push(m);
      continue;
    }
    const sid = recurringSeriesTemplateId(m);
    if (!sid) {
      out.push(m);
      continue;
    }
    if (seenSeries.has(sid)) continue;
    seenSeries.add(sid);

    const group = seriesGroups.get(sid) || [m];
    if (group.length === 1) {
      const only = group[0];
      out.push({
        ...only,
        id: sid,
        parent_meeting_id: null,
        _seriesNextOccurrenceId: only.id,
      });
      continue;
    }

    const sorted = group.slice().sort((a, b) => {
      const da = meetingScheduledTimeMs(a);
      const db = meetingScheduledTimeMs(b);
      if (Number.isNaN(da) && Number.isNaN(db)) return String(a.id).localeCompare(String(b.id));
      if (Number.isNaN(da)) return 1;
      if (Number.isNaN(db)) return -1;
      const d = da - db;
      return d !== 0 ? d : String(a.id).localeCompare(String(b.id));
    });
    const earliest = sorted[0];
    const template =
      group.find((x) => !x.parent_meeting_id && String(x.id) === sid) || group.find((x) => !x.parent_meeting_id) || earliest;
    const merged = {
      ...template,
      id: sid,
      parent_meeting_id: null,
      scheduled_date: earliest.scheduled_date,
      slot: earliest.slot != null ? earliest.slot : template.slot,
      _seriesNextOccurrenceId: earliest.id,
    };
    out.push(merged);
  }

  return out;
}

/** Normalize API meeting to reference run shape for display/filter. */
export function meetingToRun(m) {
  if (!m) return null;
  const displayTz = getConsoleRunDisplayTimezone(m);
  const { day, time, slot: derivedSlot } = getRunDayTimeSlotInTimezone(m.scheduled_date, displayTz);
  return {
    ...m,
    id: m.id,
    club_id: m.club_id,
    club_name: m.club_name,
    runName: m.name,
    day,
    slot: m.slot || derivedSlot,
    time,
    recurrence: m.recurrence_pattern,
    runType: eventTypeToRunType(m.event_type),
    effortLevel: difficultyToEffortLevel(m.difficulty),
    startLocation: m.address,
    reliability: m.reliability,
    workflowStatus: getWorkflowStatus(m),
    notes: m.what_to_expect,
    paceInfo: m.paces ? m.paces.join(" · ") : "",
    runImage: m.images && m.images.length > 0,
  };
}
